/* ==========================================================================
   ba-fit-v2.js
   A/B test: PDP vehicle-fit bar - placement + visual treatment.

   Loaded on product pages for both groups. Everything that changes the page
   is behind isVariant(), with two deliberate exceptions noted below.

   Contract with Intelligems: the variant adds `ba-fit-v2` to <html>. That's
   the whole integration surface, so this works whether IG is doing a theme
   split, a custom-code injection, or plain JS. Rollback is removing the class.

   Why the fit block is moved rather than re-rendered: <ymm-fitment> in
   theme-custom.js defines only a constructor - no connectedCallback - and its
   listeners live on `document`. A DOM move re-runs nothing and duplicates
   nothing. Rendering a second instance would double the calls to
   /apps/boost-ymm/api/fitment in BOTH groups (that helper caches on resolve
   but has no in-flight dedupe), which would contaminate the control baseline.
   Do not "simplify" this by cloning the element.
   ========================================================================== */

(function () {
  'use strict';

  var VARIANT_CLASS = 'ba-fit-v2';
  var GATE_CLASS = 'ba-fit-gate'; // set by the optional anti-flicker snippet
  var QA_KEY = 'ba_fit_v2';
  var REVEAL_FAILSAFE_MS = 2500;
  var CLASS_WATCH_MS = 10000;

  var root = document.documentElement;
  var ymmDataReady = false;
  var relocated = false;

  /* -- QA override ------------------------------------------------------- *
   * ?ba_fit_v2=1 forces the variant, ?ba_fit_v2=0 forces control. Sticks for
   * the session so you can click between PDPs. Bypasses Intelligems, so use
   * it for QA only - never for reading results.
   * --------------------------------------------------------------------- */
  try {
    var params = new URLSearchParams(window.location.search);
    if (params.has(QA_KEY)) {
      var raw = params.get(QA_KEY);
      if (raw === '0' || raw === 'false') {
        sessionStorage.removeItem(QA_KEY);
        root.classList.remove(VARIANT_CLASS);
      } else {
        sessionStorage.setItem(QA_KEY, '1');
      }
    }
    if (sessionStorage.getItem(QA_KEY) === '1') {
      root.classList.add(VARIANT_CLASS);
    }
  } catch (e) {
    /* Safari private mode refuses sessionStorage - fall through to IG */
  }

  function isVariant() {
    return root.classList.contains(VARIANT_CLASS);
  }

  /* -- Targets ----------------------------------------------------------- *
   * Resolved per product column. main-product.liquid renders product-form
   * again for the quick-buy drawer and popover, and those have no
   * .product__info wrapper - so they are never matched here, and the CSS is
   * scoped the same way. They keep the legacy treatment.
   * --------------------------------------------------------------------- */
  function eachTarget(fn) {
    var slots = document.querySelectorAll('[data-bafit-slot]');
    for (var i = 0; i < slots.length; i++) {
      var slot = slots[i];
      var column = slot.closest ? slot.closest('.product__info') : null;
      if (!column) continue;
      var wrapper = column.querySelector('[data-bafit-wrapper]');
      if (!wrapper) continue;
      fn(slot, wrapper);
    }
  }

  /* -- Reveal gate + opacity failsafe ------------------------------------ *
   * theme-custom.js drops <ymm-fitment> to opacity 0 in setVehicleValue()
   * and only restores it at the end of the fitment callback. If the YMM app
   * doesn't answer, that callback never completes and the block stays
   * invisible while still occupying space and accepting clicks.
   *
   * RUNS IN BOTH GROUPS ON PURPOSE. Fixing that only in the variant would be
   * an asymmetry: the variant would show a usable bar when the API dies and
   * control would show nothing. It only ever fires when something is already
   * broken, so it should not affect a healthy read either way.
   * --------------------------------------------------------------------- */
  function armReveal(slot, wrapper) {
    if (wrapper.getAttribute('data-bafit-armed') === 'true') return;
    wrapper.setAttribute('data-bafit-armed', 'true');

    var fit = wrapper.querySelector('ymm-fitment');
    if (!fit) {
      markReady(wrapper);
      return;
    }

    // Only the inline value is trustworthy here. Computed opacity is already
    // 1 at first paint, so checking getComputedStyle would release the gate
    // immediately and defeat the point.
    if (fit.style.opacity === '1') {
      markReady(wrapper);
      return;
    }

    var timer;
    var observer = new MutationObserver(function () {
      if (fit.style.opacity !== '1') return;
      markReady(wrapper);
      observer.disconnect();
      clearTimeout(timer);
    });
    observer.observe(fit, { attributes: true, attributeFilter: ['style'] });

    timer = setTimeout(function () {
      // data-state is "" at this point, which is the right fallback: ask for
      // the vehicle rather than leave a dead bar.
      fit.style.opacity = '1';
      markReady(wrapper);
      observer.disconnect();
    }, REVEAL_FAILSAFE_MS);
  }

  function markReady(wrapper) {
    wrapper.setAttribute('data-bafit-ready', 'true');
    root.classList.remove(GATE_CLASS);
  }

  /* -- Relocation -------------------------------------------------------- */
  function relocate() {
    if (relocated) return;
    relocated = true;
    eachTarget(function (slot, wrapper) {
      if (wrapper.parentNode !== slot) slot.appendChild(wrapper);
      adoptFitNote(wrapper);
    });
  }

  /* -- Fitment note ------------------------------------------------------ *
   * The note ships as a sibling of <ymm-fitment>, which read fine when the
   * fit line was plain text but leaves it stranded below the v2 box. Move the
   * single existing element inside the fits box rather than adding a second
   * .jsFitNote - setFitNote() writes to every match and sets inline display,
   * so a duplicate would need !important to suppress.
   *
   * Fits only, because setFitNote() populates the note only when the selected
   * vehicle matches a row in the fitment table, which is the fitted state.
   * --------------------------------------------------------------------- */
  function adoptFitNote(wrapper) {
    var note = wrapper.querySelector('.jsFitNote');
    if (!note) return;
    var body = wrapper.querySelector('.bafit--fits .bafit__body');
    if (!body || note.parentNode === body) return;
    body.appendChild(note);
  }

  /* -- Picker hydration -------------------------------------------------- *
   * The <ymm-selector> for the no-vehicle state ships inside a <template> so
   * it does not exist as a custom element in the control group at all - no
   * constructor, no localStorage reads, no joining the global
   * querySelectorAll('ymm-selector') loops in save()/resetAllNextSelectors().
   *
   * Hydration waits for ymmData:Loaded, which the header selector dispatches
   * once loadDropdownData() has resolved. By then ymmDropdownData is cached
   * module-side, so initialize() populates the selects with zero network
   * calls - the variant makes exactly as many requests as control.
   * --------------------------------------------------------------------- */
  function hydratePicker() {
    if (!isVariant() || !ymmDataReady) return;

    eachTarget(function (slot, wrapper) {
      var mount = wrapper.querySelector('.bafit__picker-mount');
      var template = wrapper.querySelector('.bafit__picker-template');
      if (!mount || !template) return;
      if (mount.getAttribute('data-bafit-hydrated') === 'true') return;

      // Nothing to pick if a vehicle is already set. If the shopper later
      // resets, data-state returns to "" and ymmData:Changed brings us back.
      var fit = wrapper.querySelector('ymm-fitment');
      if (fit && fit.getAttribute('data-state') !== '') return;

      mount.appendChild(template.content.cloneNode(true));
      mount.setAttribute('data-bafit-hydrated', 'true');

      var picker = mount.querySelector('ymm-selector');
      if (!picker || typeof picker.initialize !== 'function') {
        rollbackPicker(mount);
        return;
      }

      var pending;
      try {
        pending = picker.initialize();
      } catch (e) {
        rollbackPicker(mount);
        return;
      }
      if (pending && typeof pending.catch === 'function') {
        pending.catch(function () {
          // Dropdowns API unreachable - drop back to the jsYMMBtn fallback,
          // which opens the header selector.
          rollbackPicker(mount);
        });
      }
    });
  }

  function rollbackPicker(mount) {
    mount.removeAttribute('data-bafit-hydrated');
    while (mount.firstChild) mount.removeChild(mount.firstChild);
  }

  /* -- Alternatives link ------------------------------------------------- *
   * Mirrors what jsYMMSubmit does: the YMM collection URL plus the vehicle as
   * query params, which getYMMParamsFromURL() reads back on landing.
   *
   * Deliberately labelled "parts that fit" rather than the product type. The
   * destination is every fitting product, not tow mirrors specifically -
   * there is no product-type facet on that route - and the label should not
   * promise narrower results than the page delivers.
   * --------------------------------------------------------------------- */
  function vehicleQuery() {
    var keys = ['year', 'make', 'model'];
    var out = [];
    for (var i = 0; i < keys.length; i++) {
      var value = null;
      try {
        value = localStorage.getItem('ymm-' + keys[i]);
      } catch (e) {
        /* ignore */
      }
      if (value && value.trim()) {
        out.push(encodeURIComponent(keys[i]) + '=' + encodeURIComponent(value));
      }
    }
    return out.join('&');
  }

  function syncAltLinks() {
    var query = vehicleQuery();
    eachTarget(function (slot, wrapper) {
      var base = wrapper.getAttribute('data-bafit-alt-url') || '/collections/all';
      var href = base;
      if (query) href += (base.indexOf('?') === -1 ? '?' : '&') + query;
      var links = wrapper.querySelectorAll('[data-bafit-alt-link]');
      for (var i = 0; i < links.length; i++) {
        links[i].setAttribute('href', href);
      }
    });
  }

  /* -- Wiring ------------------------------------------------------------ */
  document.addEventListener('ymmData:Loaded', function () {
    ymmDataReady = true;
    hydratePicker();
    syncAltLinks();
  });

  document.addEventListener('ymmData:Changed', function () {
    hydratePicker();
    syncAltLinks();
  });

  function activate() {
    if (!isVariant()) return false;
    relocate();
    hydratePicker();
    return true;
  }

  function init() {
    eachTarget(armReveal);
    syncAltLinks();
    if (activate()) return;

    // Intelligems loads async, so the class can arrive after this runs.
    var observer = new MutationObserver(function () {
      if (activate()) observer.disconnect();
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    setTimeout(function () {
      observer.disconnect();
    }, CLASS_WATCH_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
